import { AlertTriangle, Download, Send } from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import { formatEuro, formatLongDate } from "../gulaConstants";
import type { AccExpense, AccSettings, AccSubmission } from "./accounting";
import { sendToGestor } from "./accountingApi";
import { buildGestorPackage } from "./gestorPackage";
import {
  periodLabel,
  type AccountingAlert,
  type AccountingSummary,
  type IncomeRow,
  type Period,
} from "./summary";
import {
  downloadAccountingFile,
  saveFile,
  uploadAccountingBytes,
} from "./useAccountingData";

/**
 * Builds the accountant's package for the period and emails it (or downloads
 * it). Shows what is still pending and when the period was sent before.
 */
export const SendToGestorDialog = ({
  open,
  onOpenChange,
  period,
  expenses,
  incomes,
  summary,
  alerts,
  settings,
  submissions,
  onSent,
  onOpenSettings,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: Period;
  expenses: AccExpense[];
  incomes: IncomeRow[];
  summary: AccountingSummary;
  alerts: AccountingAlert[];
  settings?: AccSettings;
  submissions: AccSubmission[];
  onSent: () => void;
  onOpenSettings: () => void;
}) => {
  const notify = useNotify();
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState<"send" | "download" | null>(null);

  useEffect(() => {
    if (open) setMessage("");
  }, [open]);

  const previous = submissions.filter(
    (row) =>
      row.period_year === period.year &&
      (row.period_quarter ?? null) === (period.quarter ?? null),
  );
  const pending = alerts.map((alert) => alert.text);
  const label = periodLabel(period);

  const build = () =>
    buildGestorPackage(period, expenses, incomes, downloadAccountingFile);

  const download = async () => {
    setBusy("download");
    try {
      const pack = await build();
      saveFile(pack.zip, pack.zipName, "application/zip");
      if (pack.missing.length) {
        notify(`${pack.missing.length} archivos no se han podido incluir`, {
          type: "warning",
        });
      }
    } catch (error) {
      console.error(error);
      notify("No se ha podido preparar el paquete", { type: "error" });
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    setBusy("send");
    try {
      const pack = await build();
      const folder = `packages/${period.year}-${period.quarter ? `T${period.quarter}` : "anual"}/${Date.now()}`;
      const workbookPath = await uploadAccountingBytes(
        pack.workbook,
        `${folder}/${pack.workbookName}`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      );
      const zipPath = await uploadAccountingBytes(
        pack.zip,
        `${folder}/${pack.zipName}`,
        "application/zip",
      );
      const result = await sendToGestor({
        period,
        workbookPath,
        zipPath,
        summary,
        pending: [
          ...pending,
          ...(pack.missing.length
            ? [`${pack.missing.length} archivos no se han podido incluir`]
            : []),
        ],
        message: message.trim() || undefined,
      });
      notify(`Enviado a ${result.sent_to.join(", ")}`, { type: "success" });
      onSent();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      notify(
        error instanceof Error ? error.message : "No se ha podido enviar",
        {
          type: "error",
        },
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Enviar al gestor · {label}</DialogTitle>
          <DialogDescription>
            Correo desde notificaciones@guladeagua.es con el resumen, el Excel
            de libros y un ZIP con todas las facturas y justificantes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 text-sm">
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">Para</div>
            {settings?.gestor_email ? (
              <div className="font-medium">
                {settings.gestor_name ? `${settings.gestor_name} · ` : ""}
                {settings.gestor_email}
                {settings.gestor_cc ? (
                  <span className="text-muted-foreground">
                    {" "}
                    (copia: {settings.gestor_cc})
                  </span>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                onClick={onOpenSettings}
                className="font-medium text-destructive underline-offset-2 hover:underline"
              >
                Falta el email del gestor: ponlo en Ajustes
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 rounded-lg border p-3">
            <span className="text-muted-foreground">Ingresos (base)</span>
            <span className="text-right tabular-nums">
              {formatEuro(summary.incomeBase)}
            </span>
            <span className="text-muted-foreground">Gastos (base)</span>
            <span className="text-right tabular-nums">
              {formatEuro(summary.expenseBase)} · {summary.expenseCount}
            </span>
            <span className="text-muted-foreground">
              {summary.vat.balance >= 0 ? "IVA a ingresar" : "IVA a compensar"}
            </span>
            <span className="text-right font-medium tabular-nums">
              {formatEuro(Math.abs(summary.vat.balance))}
            </span>
          </div>

          {pending.length ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
              <div className="mb-1 flex items-center gap-1 font-medium">
                <AlertTriangle className="h-4 w-4" />
                Aún falta (irá indicado en el correo)
              </div>
              <ul className="list-disc pl-5 text-xs">
                {pending.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-emerald-700 dark:text-emerald-400">
              Todo revisado y con su factura.
            </p>
          )}

          {previous.length ? (
            <p className="text-xs text-muted-foreground">
              Ya enviado:{" "}
              {previous
                .map(
                  (row) =>
                    `${formatLongDate(row.sent_at)} a ${row.to_email} (${row.expense_count} gastos)`,
                )
                .join(" · ")}
            </p>
          ) : null}

          <Textarea
            rows={3}
            placeholder="Mensaje para el gestor (opcional)"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy != null} onClick={download}>
            <Download className="h-4 w-4" />
            {busy === "download" ? "Preparando…" : "Solo descargar"}
          </Button>
          <Button
            disabled={busy != null || !settings?.gestor_email}
            onClick={send}
          >
            <Send className="h-4 w-4" />
            {busy === "send"
              ? "Enviando…"
              : previous.length
                ? "Volver a enviar"
                : "Enviar al gestor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
